import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  FileCopy as DuplicateIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Schedule as PendingIcon,
  Draw as SignatureIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import '../EmailTemplates/email-content-fix.css';
import TinyMCEEditor from '../TinyMCEEditor';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { proposalsAPI, emailsAPI, templatesAPI, signaturesAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';




const statusColors = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  accepted: 'success',
  rejected: 'error',
  expired: 'error'
};

const statusIcons = {
  draft: <EditIcon />,
  sent: <EmailIcon />,
  viewed: <ViewIcon />,
  accepted: <AcceptIcon />,
  rejected: <RejectIcon />,
  expired: <PendingIcon />
};

const ProposalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, formatCurrency, getLogoUrl } = useSettings();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({ to: '', subject: '', message: '', emailTemplateId: '', isUsingTemplate: false });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState({ fullName: '', signature: '' });
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const proposalRef = useRef();

  useEffect(() => {
    fetchProposal();
  }, [id]);

  // Periodic refresh to check for status updates (replaces SSE)
  useEffect(() => {
    if (!proposal || !id) return;

    // Only refresh for active proposals that might change status
    const activeStatuses = ['sent', 'opened', 'viewed'];
    if (!activeStatuses.includes(proposal.status)) return;

    const refreshInterval = setInterval(async () => {
      try {
        // Fetch updated proposal data
        const response = await proposalsAPI.getById(id);
        if (response.data && response.data.proposal) {
          const updatedProposal = response.data.proposal;
          
          // Check if status has changed
          if (updatedProposal.status !== proposal.status) {
            setProposal(updatedProposal);
            
            // Show notification for status change
            toast.info(`Proposal status updated to: ${updatedProposal.status.charAt(0).toUpperCase() + updatedProposal.status.slice(1)}`, {
              position: 'top-right',
              autoClose: 3000
            });
          } else {
            // Update proposal data even if status hasn't changed (for statusHistory updates)
            setProposal(updatedProposal);
          }
        }
      } catch (error) {
        console.error('Error refreshing proposal:', error);
        // Don't show error toast for periodic refresh failures to avoid spam
        // Only log the error silently
      }
    }, 15000); // 15 seconds for individual proposal view

    // Cleanup on unmount or proposal change
    return () => {
      clearInterval(refreshInterval);
    };
  }, [id, proposal?.status]);

  const fetchEmailTemplates = async () => {
    try {
      const response = await templatesAPI.getEmailTemplates({ isActive: true });
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
      toast.error('Failed to load email templates');
    }
  };

  const handleEmailTemplateChange = async (templateId) => {
    if (!templateId) {
      // Reset to default values if no template selected
      setEmailData(prev => ({
        ...prev,
        emailTemplateId: '',
        subject: `Proposal: ${proposal.title}`,
        message: `<p>Dear ${proposal.customer?.contactPerson || ''},</p><p>Please find attached our proposal for your review.</p><p>Best regards,<br/>${user.firstName} ${user.lastName}</p>`,
        isUsingTemplate: false
      }));
      return;
    }

    try {
      const response = await templatesAPI.getEmailTemplate(templateId);
      const template = response.data.template;
      
      // Replace variables in template content for display purposes only
      let subject = template.subject || '';
      let content = template.content || '';
      
      // Replace common variables (using double curly braces format)
      const variables = {
        'customer_name': proposal.customer?.contactPerson || '',
        'proposal_title': proposal.title || '',
        'user_name': `${user.firstName} ${user.lastName}`,
        'company_name': settings?.companyName || ''
      };
      
      Object.entries(variables).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        subject = subject.replace(regex, value);
        content = content.replace(regex, value);
      });
      
      setEmailData(prev => ({
        ...prev,
        emailTemplateId: templateId,
        subject: subject,
        message: content, // Show template content in editor for user customization
        isUsingTemplate: true // Flag to indicate template is being used
      }));
    } catch (error) {
      console.error('Error loading email template:', error);
      toast.error('Failed to load email template');
    }
  };

  const fetchProposal = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await proposalsAPI.getById(id);
      setProposal(response.data.proposal);
      
      // Pre-fill email data
      if (response.data.proposal.customer) {
        setEmailData({
          to: response.data.proposal.customer.email || '',
          subject: `Proposal: ${response.data.proposal.title}`,
          message: `<p>Dear ${response.data.proposal.customer.contactPerson},</p><p>Please find attached our proposal for your review.</p><p>Best regards,<br/>${user.firstName} ${user.lastName}</p>`,
          emailTemplateId: '',
          isUsingTemplate: false
        });
      }
    } catch (error) {
      console.error('Error fetching proposal:', error);
      
      // Retry logic for network errors after cache invalidation
      if ((error.code === 'ECONNABORTED' || error.message.includes('timeout') || error.request) && retryCount < 2) {
        console.log(`Retrying fetchProposal, attempt ${retryCount + 1}`);
        setTimeout(() => {
          fetchProposal(retryCount + 1);
        }, Math.pow(2, retryCount) * 1000); // Exponential backoff: 1s, 2s
        return;
      }
      
      // Only show error if it's not a network timeout or if we're not already on the page
      if (error.code !== 'ECONNABORTED' && !error.message.includes('timeout') && !error.request) {
        toast.error('Failed to load proposal');
        navigate('/proposals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/proposals/edit/${id}`);
  };

  const handleDuplicate = async () => {
    try {
      const response = await proposalsAPI.duplicate(id);
      toast.success('Proposal duplicated successfully');
      navigate(`/proposals/edit/${response.data.proposal.id}`);
    } catch (error) {
      console.error('Error duplicating proposal:', error);
      toast.error('Failed to duplicate proposal');
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await proposalsAPI.delete(id);
      toast.success('Proposal deleted successfully');
      navigate('/proposals');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setEmailLoading(true);
      const emailPayload = {
        to: emailData.to,
        subject: emailData.subject,
        emailTemplateId: emailData.emailTemplateId ? parseInt(emailData.emailTemplateId) : 0 // Always send emailTemplateId, default to 0 if none selected
      };
      
      // Include customMessage if user has modified the template content or if no template is being used
      if (!emailData.emailTemplateId || !emailData.isUsingTemplate) {
        emailPayload.customMessage = emailData.message;
      }
      
      await emailsAPI.sendProposal(id, emailPayload);
      toast.success('Email sent successfully');
      setEmailDialogOpen(false);
      
      // Add a small delay before refreshing to allow cache invalidation to complete
      setTimeout(() => {
        fetchProposal(); // Refresh to update status
      }, 1000);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      console.log(`[PDF CLIENT] Starting PDF download for proposal ${id}`);
      console.log(`[PDF CLIENT] Timestamp: ${new Date().toISOString()}`);
      setPdfLoading(true);
      toast.info('Generating PDF... This may take a few seconds.', { autoClose: false });

      // Step 1: Generate PDF on the server
      console.log(`[PDF CLIENT] Calling generatePDF API...`);
      const generateResponse = await proposalsAPI.generatePDF(id);
      
      if (!generateResponse.data?.success) {
        throw new Error(generateResponse.data?.message || 'Failed to generate PDF');
      }

      toast.info('PDF generated successfully. Downloading...', { autoClose: 2000 });

      // Step 2: Download the PDF file
      const downloadResponse = await proposalsAPI.downloadPDF(id);
      
      // Create a blob from the response
      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposal-${proposal.proposalNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('[PDF CLIENT] Error generating/downloading PDF:', error);
      console.error('[PDF CLIENT] Error status:', error.response?.status);
      console.error('[PDF CLIENT] Error data:', error.response?.data);
      console.error('[PDF CLIENT] Error timestamp:', new Date().toISOString());
      
      // Handle specific error cases
      let errorMessage = 'Failed to generate PDF';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = 'Session expired. Please login again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to download this PDF.';
            break;
          case 404:
            errorMessage = 'Proposal not found.';
            break;
          case 500:
            errorMessage = data?.message || 'Server error occurred while generating PDF.';
            break;
          default:
            errorMessage = data?.message || `Error ${status}: Failed to generate PDF.`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast.error(errorMessage);
      
      // Fallback to client-side generation if server fails
      console.log('[PDF CLIENT] Attempting fallback to client-side PDF generation...');
      console.log('[PDF CLIENT] Fallback timestamp:', new Date().toISOString());
      await handleClientSidePDF();
    } finally {
      setPdfLoading(false);
    }
  };

  /**
   * Fallback function for client-side PDF generation using html2canvas + jsPDF
   * This is used when server-side generation fails
   */
  const handleClientSidePDF = async () => {
    try {
      toast.info('Using client-side PDF generation...', { autoClose: 2000 });
      
      const element = proposalRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`proposal-${proposal.proposalNumber}.pdf`);
      toast.success('PDF downloaded successfully (client-side generation)');
    } catch (error) {
      console.error('Error in client-side PDF generation:', error);
      toast.error('Failed to generate PDF using both server and client-side methods.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSignProposal = async () => {
    try {
      setSignatureLoading(true);
      const signaturePayload = {
        signature: signatureData.fullName,
        signatureType: 'typed',
        signerName: signatureData.fullName
      };
      
      await signaturesAPI.addCompanySignature(id, signaturePayload);
      toast.success('Proposal signed successfully');
      setSignatureDialogOpen(false);
      setSignatureData({ fullName: '', signature: '' });
      
      // Add a small delay before refreshing to allow cache invalidation to complete
      setTimeout(() => {
        fetchProposal(); // Refresh to update signature status
      }, 1000);
    } catch (error) {
      console.error('Error signing proposal:', error);
      toast.error('Failed to sign proposal');
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleCreateSignature = () => {
    if (!signatureData.fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    setSignatureData(prev => ({ ...prev, signature: prev.fullName }));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Proposal: ${proposal.title}`,
          text: `Check out this proposal: ${proposal.title}`,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const calculateTotal = () => {
    if (!proposal?.items) return 0;
    return proposal.items.reduce((total, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      return total + itemTotal;
    }, 0);
  };

  const calculateTax = () => {
    const subtotal = calculateTotal();
    return subtotal * (proposal?.taxRate || 0) / 100;
  };

  const calculateGrandTotal = () => {
    return calculateTotal() + calculateTax() - (proposal?.discount || 0);
  };

  if (loading) {
    return <LoadingSpinner message="Loading proposal..." />;
  }

  if (!proposal) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Proposal not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/proposals')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              {proposal.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Proposal #{proposal.proposalNumber}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip
            title={
              <Box sx={{ 
                maxWidth: 300, 
                p: 1.5,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                borderRadius: 1
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600, 
                    mb: 1,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    pb: 0.5
                  }}
                >
                  Status History
                </Typography>
                {proposal.statusHistory && proposal.statusHistory.length > 0 ? (
                  proposal.statusHistory.map((item, index) => (
                    <Box key={index} sx={{ 
                      mb: index < proposal.statusHistory.length - 1 ? 1 : 0,
                      p: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 0.5,
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'white', 
                          fontWeight: 600,
                          mb: 0.5
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.7rem'
                        }}
                      >
                        {new Date(item.timestamp).toLocaleString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography 
                    variant="body2" 
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    No status history available
                  </Typography>
                )}
              </Box>
            }
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'transparent',
                  maxWidth: 'none',
                  p: 0
                }
              }
            }}
          >
            <Chip
              icon={statusIcons[proposal.status]}
              label={proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
              color={statusColors[proposal.status]}
              variant="outlined"
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          
          <Button
            variant="contained"
            startIcon={<SignatureIcon />}
            onClick={() => setSignatureDialogOpen(true)}
            disabled={proposal.companySignature}
            sx={{ mr: 1 }}
          >
            {proposal.companySignature ? 'Signed' : 'Sign Proposal'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={() => {
              setEmailDialogOpen(true);
              fetchEmailTemplates();
            }}
            disabled={!proposal.customer?.email || !proposal.companySignature}
          >
            Send Email
          </Button>
          
          <Button
            variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            {pdfLoading ? 'Generating...' : 'Download PDF'}
          </Button>
          
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Proposal Content */}
      <Paper ref={proposalRef} sx={{ p: 4, mb: 3 }}>
        {/* Company Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {settings.companyLogo && (
            <Box sx={{ mb: 2 }}>
              <img
                src={getLogoUrl()}
                alt={settings.companyName}
                style={{ maxHeight: '80px', maxWidth: '200px' }}
              />
            </Box>
          )}
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {settings.companyName || 'Your Company'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {settings.companyAddress && (
              <>
                {settings.companyAddress}<br />
                {settings.companyCity && `${settings.companyCity}, `}
                {settings.companyState} {settings.companyZip}<br />
              </>
            )}
            {settings.companyPhone && `Phone: ${settings.companyPhone}`}
            {settings.companyEmail && ` | Email: ${settings.companyEmail}`}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Proposal Info */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Proposal Information
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Proposal Number:</strong> {proposal.proposalNumber}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Date:</strong> {format(new Date(proposal.createdAt), 'dd/MM/yyyy')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Valid Until:</strong> {proposal.validUntil ? format(new Date(proposal.validUntil), 'dd/MM/yyyy') : 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Status:</strong> {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            {proposal.customer ? (
              <>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {proposal.customer.contactPerson}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email:</strong> {proposal.customer.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Phone:</strong> {proposal.customer.phone || 'N/A'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Company:</strong> {proposal.customer.companyName || 'N/A'}
                </Typography>
                {proposal.customer.address && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Address:</strong><br />
                    {proposal.customer.address}<br />
                    {proposal.customer.city && `${proposal.customer.city}, `}
                    {proposal.customer.state} {proposal.customer.zipCode}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No customer assigned
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Description */}
        {proposal.description && (
          <>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
              {proposal.description}
            </Typography>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Items */}
        {proposal.items && proposal.items.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Items
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="center"><strong>Quantity</strong></TableCell>
                    <TableCell align="right"><strong>Unit Price</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposal.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.description}
                        </Typography>
                        {item.details && (
                          <Typography variant="caption" color="text.secondary">
                            {item.details}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Totals */}
                  <TableRow>
                    <TableCell colSpan={3} align="right"><strong>Subtotal:</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(calculateTotal())}</strong></TableCell>
                  </TableRow>
                  
                  {proposal.taxRate > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right">Tax ({proposal.taxRate}%):</TableCell>
                      <TableCell align="right">{formatCurrency(calculateTax())}</TableCell>
                    </TableRow>
                  )}
                  
                  {proposal.discount > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right">Discount:</TableCell>
                      <TableCell align="right">-{formatCurrency(proposal.discount)}</TableCell>
                    </TableRow>
                  )}
                  
                  <TableRow>
                    <TableCell colSpan={3} align="right"><strong>Total:</strong></TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" fontWeight={600}>
                        {formatCurrency(calculateGrandTotal())}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Content */}
        {proposal.content && (
          <>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Box
              className="proposal-content-display"
              sx={{ mb: 3 }}
              dangerouslySetInnerHTML={{ __html: proposal.content }}
            />
          </>
        )}

        {/* Notes */}
        {proposal.notes && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {proposal.notes}
            </Typography>
          </>
        )}

        {/* Signatures Section */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="h6" gutterBottom>
            Signatures
          </Typography>
          
          <Grid container spacing={4}>
            {/* Company Signature */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Company Representative
                </Typography>
                
                {proposal.companySignature ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Signed by: {JSON.parse(proposal.companySignature).signerName}
                    </Typography>
                    <Box sx={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      p: 2, 
                      textAlign: 'center',
                      backgroundColor: '#f9f9f9',
                      mb: 1
                    }}>
                      <Typography variant="h5" sx={{ 
                        fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                        color: 'primary.main',
                        fontWeight: 400,
                        fontSize: '1.8rem',
                        fontStyle: 'italic'
                      }}>
                        {JSON.parse(proposal.companySignature).signature}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Signed on: {new Date(JSON.parse(proposal.companySignature).signedAt).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No company signature
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            
            {/* Customer Signature */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Customer
                </Typography>
                
                {proposal.customerSignature ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Signed by: {JSON.parse(proposal.customerSignature).signerName}
                    </Typography>
                    <Box sx={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      p: 2, 
                      textAlign: 'center',
                      backgroundColor: '#f9f9f9',
                      mb: 1
                    }}>
                      <Typography variant="h5" sx={{ 
                        fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                        color: 'primary.main',
                        fontWeight: 400,
                        fontSize: '1.8rem',
                        fontStyle: 'italic'
                      }}>
                        {JSON.parse(proposal.customerSignature).signature}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Signed on: {new Date(JSON.parse(proposal.customerSignature).signedAt).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No customer signature
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            Thank you for your business!
          </Typography>
          {settings.companyWebsite && (
            <Typography variant="body2" color="text.secondary">
              Visit us at: {settings.companyWebsite}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => { handleEdit(); handleMenuClose(); }}>
          <ListItemIcon><EditIcon /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => { handleDuplicate(); handleMenuClose(); }}>
          <ListItemIcon><DuplicateIcon /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => { handlePrint(); handleMenuClose(); }}>
          <ListItemIcon><PrintIcon /></ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => { handleShare(); handleMenuClose(); }}>
          <ListItemIcon><ShareIcon /></ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItemComponent>
        
        <Divider />
        
        <MenuItemComponent 
          onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItemComponent>
      </Menu>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Proposal via Email</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="To"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Email Template (Optional)</InputLabel>
                <Select
                  value={emailData.emailTemplateId}
                  onChange={(e) => handleEmailTemplateChange(e.target.value)}
                  label="Email Template (Optional)"
                >
                  <MenuItem value="">No Template</MenuItem>
                  {emailTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Subject"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Message *
              </Typography>
              <TinyMCEEditor
                value={emailData.message}
                onChange={(value) => setEmailData({ ...emailData, message: value })}
                placeholder="Enter your email message here..."
                height={400}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={emailLoading || !emailData.to || !emailData.subject}
          >
            {emailLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Proposal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this proposal? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <SignatureIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" component="div">
            Sign Proposal: {proposal?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please add your signature to authorize this proposal before sending it to the customer.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SignatureIcon sx={{ mr: 1 }} />
              Digital Signature
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
              Type your full name to create signature
            </Typography>
            
            <TextField
              fullWidth
              placeholder="Enter your full name"
              value={signatureData.fullName}
              onChange={(e) => setSignatureData(prev => ({ ...prev, fullName: e.target.value }))}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            
            <Button
              fullWidth
              variant="contained"
              startIcon={<SignatureIcon />}
              onClick={handleCreateSignature}
              disabled={!signatureData.fullName.trim()}
              sx={{ mb: 2, py: 1.5 }}
            >
              Create Digital Signature
            </Button>
            
            {signatureData.signature && (
              <Box sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 1, 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ 
                   fontFamily: '"Brush Script MT", "Lucida Handwriting", "Segoe Print", cursive',
                   color: 'primary.main',
                   fontWeight: 400,
                   fontSize: '1.8rem',
                   fontStyle: 'italic'
                 }}>
                  {signatureData.signature}
                </Typography>
              </Box>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              This digital signature includes timestamp verification and is legally equivalent to a handwritten signature.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => {
            setSignatureDialogOpen(false);
            setSignatureData({ fullName: '', signature: '' });
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSignProposal}
            variant="contained"
            disabled={signatureLoading || !signatureData.signature}
            sx={{ minWidth: 120 }}
          >
            {signatureLoading ? 'Signing...' : 'Sign Proposal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Quick Actions */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleEdit}
      >
        <EditIcon />
      </Fab>
    </Box>
  );
};

export default ProposalView;